import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Megaphone, Send, Eye, MailCheck, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const DOW = [["Lun", 0], ["Mar", 1], ["Mer", 2], ["Gio", 3], ["Ven", 4], ["Sab", 5], ["Dom", 6]];
const ROLE_LABEL = { admin: "Admin", commercial_manager: "Resp. Commerciale", head_of_partner: "Head of Partner", back_office: "Back Office" };

export const AgencyFunnelPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [prevAgency, setPrevAgency] = useState("");

  const load = () => api.get("/agency-funnel-config").then((r) => { setCfg(r.data); if (!prevAgency && (r.data.agencies_disponibili || [])[0]) setPrevAgency(r.data.agencies_disponibili[0].id); }).catch(() => setCfg(false));
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (cfg === null) return <Card className="p-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></Card>;
  if (cfg === false) return null;

  const up = (patch) => setCfg((s) => ({ ...s, ...patch }));
  const toggleDay = (d) => up({ giorni: cfg.giorni.includes(d) ? cfg.giorni.filter((x) => x !== d) : [...cfg.giorni, d].sort() });
  const toggleAgency = (id) => up({ agencies: cfg.agencies.includes(id) ? cfg.agencies.filter((x) => x !== id) : [...cfg.agencies, id] });
  const toggleRole = (r) => up({ cc_roles: cfg.cc_roles.includes(r) ? cfg.cc_roles.filter((x) => x !== r) : [...cfg.cc_roles, r] });

  const save = async () => {
    setSaving(true);
    try {
      const body = { attivo: cfg.attivo, agencies: cfg.agencies, giorni: cfg.giorni, send_time: cfg.send_time,
        include_kpi: cfg.include_kpi, include_impegni: cfg.include_impegni, subject: cfg.subject, mittente: cfg.mittente, template: cfg.template,
        cc_roles: cfg.cc_roles, cc_emails: (cfg.cc_emails || []) };
      const r = await api.put("/agency-funnel-config", body);
      setCfg((s) => ({ ...r.data, agencies_disponibili: s.agencies_disponibili, roles_disponibili: s.roles_disponibili }));
      toast.success("Funnel agenzie salvato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const doPreview = async () => {
    try {
      const r = await api.post("/agency-funnel-config/anteprima", { agency_id: prevAgency, include_kpi: cfg.include_kpi, include_impegni: cfg.include_impegni, subject: cfg.subject, template: cfg.template });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Anteprima non disponibile"); }
  };

  const sendNow = async () => {
    if (!window.confirm("Inviare ORA il funnel alle agenzie selezionate?")) return;
    try { await api.post("/agency-funnel-config/invia-ora"); toast.success("Invio avviato"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
  };

  const sendTest = async () => {
    try {
      const r = await api.post("/agency-funnel-config/invia-prova", { agency_id: prevAgency, include_kpi: cfg.include_kpi, include_impegni: cfg.include_impegni, subject: cfg.subject, template: cfg.template });
      toast.success(`Email di prova inviata a ${r.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio prova"); }
  };

  const ccEmailsStr = (cfg.cc_emails || []).join(", ");

  return (
    <Card className="p-6" data-testid="agency-funnel-panel">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-left" data-testid="funnel-toggle">
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <Megaphone className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          <h2 className="font-heading text-lg font-bold tracking-tight text-emerald-700 dark:text-emerald-400">Funnel agenzie — Impegni & KPI</h2>
        </button>
        <Badge variant={cfg.attivo ? "default" : "outline"} className="text-[10px]">{cfg.attivo ? "ATTIVO" : "SPENTO"}</Badge>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm font-semibold">
          <Checkbox checked={cfg.attivo} onCheckedChange={(v) => up({ attivo: !!v })} data-testid="funnel-attivo" /> Attiva funnel
        </label>
      </div>
      {open && (<>
      <p className="mb-4 mt-4 text-xs text-muted-foreground">Invia a ogni agenzia selezionata una email di testo (senza HTML) con i propri impegni di chiusura e i KPI del periodo. Destinatari: gli agenti dell'agenzia. Puoi aggiungere destinatari in copia (CC) e scegliere i giorni della settimana.</p>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Agenzie destinatarie</div>
          <div className="mb-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => up({ agencies: [] })} data-testid="funnel-all-agencies">Tutte</Button>
            <span className="self-center text-xs text-muted-foreground">{cfg.agencies.length === 0 ? "tutte le agenzie" : `${cfg.agencies.length} selezionate`}</span>
          </div>
          <div className="max-h-44 overflow-y-auto rounded-md border border-border p-2">
            {(cfg.agencies_disponibili || []).map((a) => (
              <label key={a.id} className="flex cursor-pointer items-center gap-2 py-0.5 text-sm">
                <Checkbox checked={cfg.agencies.includes(a.id)} onCheckedChange={() => toggleAgency(a.id)} data-testid={`funnel-agency-${a.id}`} /> {a.name}
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Giorni di invio</div>
            <div className="flex flex-wrap gap-1.5">
              {DOW.map(([lab, d]) => (
                <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`funnel-dow-${d}`}
                  className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${cfg.giorni.includes(d) ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}>{lab}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Ora</div>
              <Input type="time" value={cfg.send_time} onChange={(e) => up({ send_time: e.target.value })} className="h-9 w-[120px]" data-testid="funnel-time" />
            </div>
            <div className="flex flex-col gap-1 pt-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={cfg.include_impegni} onCheckedChange={(v) => up({ include_impegni: !!v })} data-testid="funnel-inc-impegni" /> Includi impegni</label>
              <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={cfg.include_kpi} onCheckedChange={(v) => up({ include_kpi: !!v })} data-testid="funnel-inc-kpi" /> Includi KPI</label>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Mittente</div>
          <Input value={cfg.mittente || ""} onChange={(e) => up({ mittente: e.target.value })} placeholder="Michele Torri" data-testid="funnel-mittente" />
        </div>
        <div>
          <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Oggetto email</div>
          <Input value={cfg.subject} onChange={(e) => up({ subject: e.target.value })} data-testid="funnel-subject" />
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">In copia (CC)</div>
          <div className="mb-2 flex flex-wrap gap-3">
            {(cfg.roles_disponibili || []).map((r) => (
              <label key={r} className="flex cursor-pointer items-center gap-1.5 text-sm"><Checkbox checked={cfg.cc_roles.includes(r)} onCheckedChange={() => toggleRole(r)} data-testid={`funnel-cc-role-${r}`} /> {ROLE_LABEL[r] || r}</label>
            ))}
          </div>
          <Input defaultValue={ccEmailsStr} onBlur={(e) => up({ cc_emails: e.target.value.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@")) })} placeholder="Email in copia (separate da virgola)" className="h-9" data-testid="funnel-cc-emails" />
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Modello email (modificabile)</div>
        <textarea value={cfg.template || ""} onChange={(e) => up({ template: e.target.value })} rows={9}
          className="w-full rounded-md border border-border bg-background p-2 font-mono text-xs" data-testid="funnel-template" />
        <p className="mt-1 text-[11px] text-muted-foreground">Variabili: <code>{"{agenzia}"}</code> nome agenzia · <code>{"{periodo}"}</code> periodo KPI · <code>{"{kpi}"}</code> blocco KPI · <code>{"{impegni}"}</code> blocco impegni. I blocchi si popolano in automatico per ogni agenzia.</p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={saving} data-testid="funnel-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Salva</Button>
        <div className="flex items-center gap-1">
          <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={prevAgency} onChange={(e) => setPrevAgency(e.target.value)} data-testid="funnel-preview-agency">
            {(cfg.agencies_disponibili || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <Button variant="outline" onClick={doPreview} data-testid="funnel-preview-btn"><Eye className="mr-1 h-4 w-4" /> Anteprima</Button>
        </div>
        <Button variant="outline" onClick={sendTest} data-testid="funnel-send-test"><MailCheck className="mr-1 h-4 w-4" /> Invia prova a me</Button>
        <Button variant="outline" onClick={sendNow} data-testid="funnel-send-now"><Send className="mr-1 h-4 w-4" /> Invia ora a tutti</Button>
      </div>

      {preview && (
        <div className="mt-4 rounded-md border border-border bg-muted/30 p-3" data-testid="funnel-preview">
          <div className="mb-1 text-xs text-muted-foreground">Oggetto: <span className="font-semibold text-foreground">{preview.subject}</span> · A: {(preview.destinatari || []).join(", ") || "— nessuna email agente —"}</div>
          <pre className="whitespace-pre-wrap font-sans text-sm">{preview.testo}</pre>
        </div>
      )}
      </>)}
    </Card>
  );
};
